import type { WatchlistCategory, WatchlistItem } from './stockTypes';

/**
 * 관심종목 탭 구성. (3.10)
 *
 * 화면에서 떼어낸 이유
 *   "어떤 탭을 그리나" 와 "지운 탭을 보고 있으면 어디로 가나" 는
 *   조건이 여러 개 겹치는 판단인데, 화면에 두면 렌더링해야만 확인할 수 있다.
 *   특히 마지막 카테고리를 지우는 순간처럼 짧게 스치는 상태는 눈으로 잡히지 않는다.
 *
 * 🔴 한 종목이 여러 카테고리에 속한다
 *   그래서 "이 종목의 카테고리" 가 아니라 "이 카테고리에 이 종목이 있나" 로 판정한다.
 */

/** 전체 탭. 모든 관심종목을 보여준다 */
export const ALL_TAB = 'ALL';

/** 미분류 탭. 어느 카테고리에도 넣지 않은 종목이다 */
export const UNCATEGORIZED_TAB = 'UNCATEGORIZED';

/** 카테고리 탭은 숫자(카테고리 id)다 */
export type WatchlistTabKey = typeof ALL_TAB | typeof UNCATEGORIZED_TAB | number;

export interface WatchlistTab {
  key: WatchlistTabKey;
  label: string;
  count: number;
}

/** 이 종목이 그 카테고리에 들어 있나 */
export function isInCategory(item: WatchlistItem, categoryId: number): boolean {
  return item.categories.some((ref) => ref.categoryId === categoryId);
}

/** 어느 카테고리에도 안 들어 있나 */
export function isUncategorized(item: WatchlistItem): boolean {
  return item.categories.length === 0;
}

/**
 * 그릴 탭 목록을 만든다.
 *
 * 규칙 세 가지
 *
 *   카테고리가 하나도 없으면 탭을 아예 그리지 않는다
 *     탭 하나짜리 줄은 자리만 차지하고 아무것도 고르게 해주지 않는다.
 *
 *   비어 있는 카테고리도 그린다
 *     사용자가 직접 만든 것이다. 종목을 다 빼면 탭이 사라진다면
 *     "내가 만든 게 없어졌다" 로 읽힌다.
 *
 *   미분류는 해당 종목이 있을 때만 그린다
 *     전부 분류해 두었는데 빈 미분류 탭이 남아 있으면 눌러볼 이유가 없는 탭이 된다.
 *
 * 🔴 카테고리 건수의 합은 전체와 다를 수 있다
 *   한 종목이 두 카테고리에 들어 있으면 양쪽에서 센다. 그것이 사실이다.
 */
export function buildWatchlistTabs(
  items: WatchlistItem[],
  categories: WatchlistCategory[],
): WatchlistTab[] {
  if (categories.length === 0) {
    return [];
  }

  const uncategorized = items.filter(isUncategorized).length;

  const tabs: WatchlistTab[] = [
    { key: ALL_TAB, label: '전체', count: items.length },
    ...categories.map((category) => ({
      key: category.id,
      label: category.name,
      count: items.filter((item) => isInCategory(item, category.id)).length,
    })),
  ];

  if (uncategorized > 0) {
    tabs.push({ key: UNCATEGORIZED_TAB, label: '미분류', count: uncategorized });
  }

  return tabs;
}

/**
 * 선택한 탭에 해당하는 종목만 남기고 그 탭의 순서로 줄을 세운다.
 *
 * 🔴 카테고리 탭은 정렬 기준이 다르다
 *   전체·미분류는 관심종목 자체의 sortOrder 를 쓰고(서버가 이미 그 순서로 준다),
 *   카테고리 탭은 그 카테고리 안의 sortOrder 를 쓴다.
 *   같은 종목이 A 에서는 첫 줄, C 에서는 다섯째 줄일 수 있기 때문이다.
 */
export function filterByTab(
  items: WatchlistItem[],
  tab: WatchlistTabKey,
): WatchlistItem[] {
  if (tab === ALL_TAB) {
    return items;
  }
  if (tab === UNCATEGORIZED_TAB) {
    return items.filter(isUncategorized);
  }

  return items
    .filter((item) => isInCategory(item, tab))
    .sort((a, b) => orderIn(a, tab) - orderIn(b, tab));
}

/**
 * 지금 고른 탭이 아직 존재하는지 확인하고, 없으면 전체로 되돌린다.
 *
 * 🔴 이것이 없으면 빈 화면이 남는다
 *   보고 있던 카테고리를 관리 모달에서 지우면 그 탭이 목록에서 사라지는데,
 *   선택 상태는 그 id 를 그대로 들고 있다. 어느 탭도 활성으로 보이지 않고
 *   목록은 0건이 된다. 사용자는 종목이 함께 지워졌다고 오해한다.
 *
 * 미분류를 보다가 마지막 미분류 종목을 분류했을 때도 같은 일이 생긴다.
 */
export function resolveTab(
  tabs: WatchlistTab[],
  selected: WatchlistTabKey,
): WatchlistTabKey {
  return tabs.some((tab) => tab.key === selected) ? selected : ALL_TAB;
}

/**
 * 편집 모드에서 한 칸 옮긴 결과를 돌려준다. 원본은 건드리지 않는다.
 *
 * 드래그가 아니라 위·아래 버튼을 쓰는 이유는 docs/impl/54 에 있다 —
 * HTML5 drag & drop 은 터치에서 아예 동작하지 않고, 라이브러리를 넣으면
 * 폰과 PC 에서 서로 다른 코드 경로를 유지하게 된다.
 *
 * 범위를 벗어나면 원본을 그대로 준다. 호출부가 첫 줄·마지막 줄을 따로 막지 않아도 된다.
 */
export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return items;
  }

  const moved = [...items];
  const [picked] = moved.splice(index, 1);
  moved.splice(target, 0, picked as T);
  return moved;
}

// ─────────────────────────────────────────────

/** 그 카테고리 안에서의 순서. 소속이 없으면 맨 뒤로 보낸다 */
function orderIn(item: WatchlistItem, categoryId: number): number {
  const ref = item.categories.find((entry) => entry.categoryId === categoryId);
  return ref === undefined ? Number.MAX_SAFE_INTEGER : ref.sortOrder;
}
